import { useDispatch } from "react-redux";
import { useRef, useCallback } from "react";
import { AppDispatch } from "app/store";
import { MapModel } from "common/model";
import { actions } from "features/builder/slice";
import { CommandManager } from "features/builder/engine";
import { ActionCommand, ActionCommandType } from "features/builder/types";

export const useActionCommand = () => {
  const dispatch = useDispatch<AppDispatch>();
  const commandManager = useRef(new CommandManager());

  // アクション実行（履歴記録付き）
  const execute = useCallback(
    (command: ActionCommand) => {
      switch (command.type) {
        case ActionCommandType.ADD_MODE: {
          dispatch(actions.addModelToMap(command.model));
          // Undo用に記録
          const record = commandManager.current.recordAdd(command.model);
          dispatch(actions.pushUndo(record));
          dispatch(actions.clearRedo());
          break;
        }
        case ActionCommandType.REMOVE_MODE: {
          // 削除前のモデルを取得してUndoに記録
          // Note: このフックではstoreにアクセスできないので、
          // モデル情報はコンポーネント側で渡す必要がある
          dispatch(actions.removeModelFromMap(command.meshId));
          break;
        }
        case ActionCommandType.CHANGE_MODE: {
          dispatch(
            actions.updateMapModel({
              id: command.id,
              changes: command.changes,
            }),
          );
          break;
        }
        default:
          break;
      }
    },
    [dispatch],
  );

  // Undo実行
  const undo = useCallback(() => {
    const record = commandManager.current.undo();
    if (!record) return null;

    // backward操作を実行
    switch (record.type) {
      case ActionCommandType.ADD_MODE:
        // ADDの取り消し = 削除
        if (record.backward.meshId) {
          dispatch(actions.removeModelFromMap(record.backward.meshId));
        }
        break;
      case ActionCommandType.REMOVE_MODE:
        // REMOVEの取り消し = 追加
        if (record.backward.model) {
          dispatch(actions.addModelToMap(record.backward.model));
        }
        break;
      case ActionCommandType.CHANGE_MODE:
        // CHANGEの取り消し = 前の値に戻す
        if (record.backward.id && record.backward.changes) {
          dispatch(
            actions.updateMapModel({
              id: record.backward.id,
              changes: record.backward.changes,
            }),
          );
        }
        break;
    }

    dispatch(actions.popUndo());
    dispatch(actions.pushRedo(record));

    return record;
  }, [dispatch]);

  // Redo実行
  const redo = useCallback(() => {
    const record = commandManager.current.redo();
    if (!record) return null;

    // forward操作を再実行
    switch (record.type) {
      case ActionCommandType.ADD_MODE:
        if (record.forward.model) {
          dispatch(actions.addModelToMap(record.forward.model));
        }
        break;
      case ActionCommandType.REMOVE_MODE:
        if (record.forward.meshId) {
          dispatch(actions.removeModelFromMap(record.forward.meshId));
        }
        break;
      case ActionCommandType.CHANGE_MODE:
        if (record.forward.id && record.forward.changes) {
          dispatch(
            actions.updateMapModel({
              id: record.forward.id,
              changes: record.forward.changes,
            }),
          );
        }
        break;
    }

    dispatch(actions.popRedo());
    dispatch(actions.pushUndo(record));

    return record;
  }, [dispatch]);

  // 削除時に履歴記録（モデル情報付き）
  const executeRemove = useCallback(
    (model: MapModel) => {
      dispatch(actions.removeModelFromMap(model.id));
      const record = commandManager.current.recordRemove(model);
      dispatch(actions.pushUndo(record));
      dispatch(actions.clearRedo());
    },
    [dispatch],
  );

  // 変更時に履歴記録（前後の値付き）
  const executeChange = useCallback(
    (
      id: string,
      newChanges: { [key: string]: unknown },
      oldChanges: { [key: string]: unknown },
    ) => {
      dispatch(
        actions.updateMapModel({
          id,
          changes: newChanges as { [key: string]: string },
        }),
      );
      const record = commandManager.current.recordChange(
        id,
        newChanges as { [key: string]: string },
        oldChanges as { [key: string]: string },
      );
      dispatch(actions.pushUndo(record));
      dispatch(actions.clearRedo());
    },
    [dispatch],
  );

  // Undo/Redo可能かチェック
  const canUndo = useCallback(() => commandManager.current.canUndo(), []);
  const canRedo = useCallback(() => commandManager.current.canRedo(), []);

  // 履歴クリア
  const clearHistory = useCallback(() => {
    commandManager.current.clear();
    dispatch(actions.clearHistory());
  }, [dispatch]);

  // 後方互換性：既存コードで handleAction(command) として使用可能
  const handleAction = execute;

  return {
    execute,
    handleAction, // 後方互換性のエイリアス
    executeRemove,
    executeChange,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
  };
};
